import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { SectionHeading } from '../components/ui/SectionHeading';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, BookOpen, Heart, Target, Eye, Compass, Star } from 'lucide-react';
interface FaithStatement {
  id: number;
  title: string;
  content: string;
  scripture?: string;
}
const faithStatements: FaithStatement[] = [{
  id: 1,
  title: 'The Scripture',
  content: 'We believe the Bible to be the inspired, the only infallible, authoritative Word of God. When rightly divided and properly interpreted, it provides sufficient revelation for the church to be complete in doctrine, maturity, ministry and worship.',
  scripture: 'II Timothy 3:16; I Thess. 2:13; II Peter 1:21'
}, {
  id: 2,
  title: 'The Godhead',
  content: 'We believe that there is one God, eternally existent in three persons: Father, Son, and Holy Spirit.',
  scripture: 'Deut. 6:4; Isaiah 43:10; Matt. 28:19; Luke 3:22'
}, {
  id: 3,
  title: 'The Deity of Christ',
  content: 'We believe in the deity of our Lord Jesus Christ, in His virgin birth, in His sinless life, in His miracles, in His vicarious and atoning death through His shed blood, in His bodily resurrection, in His ascension to the right hand of the Father, and in His personal return in power and glory.',
  scripture: 'John 1:1-4; Matt. 1:23; Heb. 7:26; I Cor. 15:3; Acts 1:9'
}, {
  id: 4,
  title: 'Salvation',
  content: 'We believe that for the salvation of lost and sinful man, regeneration by the Holy Spirit is absolutely essential.',
  scripture: 'John 3:3; Rom. 10:13-15; Eph. 2:8'
}, {
  id: 5,
  title: 'The Holy Spirit',
  content: 'We believe in the present ministry of the Holy Spirit by whose indwelling the Christian is enabled to live a godly life. We believe in the baptism of the Holy Spirit with the evidence of speaking in other tongues as the Spirit gives utterance.',
  scripture: 'Acts 2:4; Rom. 8:16; I Cor. 12:1-31'
}, {
  id: 6,
  title: 'Resurrection',
  content: 'We believe in the resurrection of both the saved and the lost; they that are saved unto the resurrection of life and they that are lost unto the resurrection of damnation.',
  scripture: 'John 5:28-29; Rev. 20:11-15'
}, {
  id: 7,
  title: 'Spiritual Unity',
  content: 'We believe in the spiritual unity of believers in our Lord Jesus Christ.',
  scripture: 'Eph. 4:1-6; Phil. 2:1-4'
}, {
  id: 8,
  title: 'Divine Healing',
  content: 'We believe that divine healing is an integral part of the gospel. Deliverance from sickness is provided for in the atonement, and is the privilege of all believers.',
  scripture: 'Isaiah 53:4-5; Matt. 8:16-17; James 5:14-16'
}, {
  id: 9,
  title: 'The Blessed Hope',
  content: "We believe in the blessed hope—the rapture of the Church at Christ's coming.",
  scripture: 'Titus 2:13; I Thess. 4:16-17; Rom. 8:23'
}, {
  id: 10,
  title: 'The Church',
  content: 'We believe that the Church is the body of Christ, the habitation of God through the Spirit, with divine appointments for the fulfillment of her great commission.',
  scripture: 'Eph. 1:22-23; Eph. 2:22; Heb. 12:23'
}, {
  id: 11,
  title: 'Ordinances',
  content: "We believe in the ordinances of the church: Water Baptism by immersion and Holy Communion (The Lord's Supper).",
  scripture: 'Matt. 28:19; Rom. 6:4; I Cor. 11:26'
}, {
  id: 12,
  title: 'Sanctification',
  content: 'We believe in sanctification—an act of separation from that which is evil, and of dedication unto God.',
  scripture: 'Rom. 12:1-2; I Thess. 5:23; Heb. 13:12'
}];
const coreValues = [{
  letter: 'R',
  word: 'Respect',
  scripture: 'Phil. 2:3'
}, {
  letter: 'E',
  word: 'Excellence',
  scripture: 'Col. 3:23'
}, {
  letter: 'L',
  word: 'Love',
  scripture: 'John 13:34'
}, {
  letter: 'A',
  word: 'Accountability',
  scripture: 'Matt. 25:14-33'
}, {
  letter: 'T',
  word: 'Transparency',
  scripture: 'Rom. 12:10'
}, {
  letter: 'E',
  word: 'Empowerment',
  scripture: 'Deut. 8:18'
}];
export function WhatWeBelievePage() {
  const [expandedId, setExpandedId] = useState<number | null>(1);
  return <Layout data-id="element-1074">
      {/* Hero Section */}
      <div className="bg-[#0A1F44] pt-44 md:pt-48 pb-20 text-white relative overflow-hidden" data-id="element-1075">
        <div className="absolute inset-0 opacity-10" data-id="element-1076">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-1077" />
        </div>
        <div className="container-custom relative z-10" data-id="element-1078">
          <div className="max-w-3xl" data-id="element-1079">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block text-[#C8102E] text-sm font-semibold tracking-widest uppercase mb-4" data-id="element-1080">
              Our Foundation
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-6xl font-serif font-bold mb-6" data-id="element-1081">
              What We Believe
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-xl text-white/80 leading-relaxed" data-id="element-1082">
              Our faith is rooted in the timeless truths of Scripture. Discover
              the core beliefs that guide our church family and shape our
              ministry.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Purpose Section */}
      <section className="py-20 bg-[#F5F7FA]" data-id="element-1083">
        <div className="container-custom" data-id="element-1084">
          <div className="max-w-4xl mx-auto" data-id="element-1085">
            <div className="text-center mb-12" data-id="element-1086">
              <Compass className="w-12 h-12 text-[#C8102E] mx-auto mb-6" data-id="element-1087" />
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-1088">
                Our Purpose
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed" data-id="element-1089">
                To develop leaders and world changers within the family of God,
                empowered with roots for growth and wings for flight.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12" data-id="element-1090">
              {['Evangelism', 'Worship', 'Fellowship', 'Ministry', 'Discipleship'].map((purpose, index) => <motion.div key={purpose} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-white p-6 rounded-xl text-center shadow-sm" data-id="element-1091">
                  <div className="w-12 h-12 bg-[#0A1F44] rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold" data-id="element-1092">
                    {index + 1}
                  </div>
                  <p className="font-semibold text-[#0A1F44]" data-id="element-1093">{purpose}</p>
                </motion.div>)}
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-md" data-id="element-1094">
              <p className="text-gray-700 text-lg leading-relaxed text-center" data-id="element-1095">
                The purpose of Household of Christ Church is:{' '}
                <strong className="text-[#0A1F44]" data-id="element-1096">
                  to engage, enrich, enlighten, equip and empower
                </strong>{' '}
                people with roots for growth and wings for flight, in accordance
                with the great commission (Matt. 28:18).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Statements Section */}
      <section className="py-20 bg-white" data-id="element-1097">
        <div className="container-custom" data-id="element-1098">
          <SectionHeading title="Our Statements" subtitle="Who We Are" data-id="element-1099" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1100">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-[#0A1F44] p-8 rounded-2xl text-white" data-id="element-1101">
              <div className="w-14 h-14 bg-[#C8102E] rounded-full flex items-center justify-center mb-6" data-id="element-1102">
                <Eye className="w-7 h-7 text-white" data-id="element-1103" />
              </div>
              <h3 className="text-2xl font-serif font-bold mb-4" data-id="element-1104">Our Vision</h3>
              <p className="text-white/90 leading-relaxed" data-id="element-1105">
                To develop leaders and world changers within the family of God,
                empowered with roots for growth and wings for flight.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-[#C8102E] p-8 rounded-2xl text-white" data-id="element-1106">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-6" data-id="element-1107">
                <Target className="w-7 h-7 text-[#C8102E]" data-id="element-1108" />
              </div>
              <h3 className="text-2xl font-serif font-bold mb-4" data-id="element-1109">
                Our Mission
              </h3>
              <p className="text-white/90 leading-relaxed" data-id="element-1110">
                To enlighten, engage, equip, enrich and empower individuals; to
                discover and maximize their God-given potential.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Faith Statements Accordion */}
      <section className="py-20 bg-[#F5F7FA]" data-id="element-1111">
        <div className="container-custom" data-id="element-1112">
          <SectionHeading title="Statement of Faith" subtitle="What We Believe" data-id="element-1113" />

          <div className="max-w-4xl mx-auto space-y-4 mt-12" data-id="element-1114">
            {faithStatements.map((statement, index) => <motion.div key={statement.id} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-xl overflow-hidden shadow-sm" data-id="element-1115">
                <button onClick={() => setExpandedId(expandedId === statement.id ? null : statement.id)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors" data-id="element-1116">
                  <div className="flex items-center gap-4" data-id="element-1117">
                    <span className="w-8 h-8 bg-[#0A1F44] text-white rounded-full flex items-center justify-center text-sm font-bold shrink-0" data-id="element-1118">
                      {statement.id}
                    </span>
                    <h3 className="font-semibold text-[#0A1F44] text-lg" data-id="element-1119">
                      {statement.title}
                    </h3>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expandedId === statement.id ? 'rotate-180' : ''}`} data-id="element-1120" />
                </button>
                <AnimatePresence data-id="element-1121">
                  {expandedId === statement.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-1122">
                      <div className="px-6 pb-6 border-t border-gray-100 pt-4" data-id="element-1123">
                        <p className="text-gray-600 leading-relaxed mb-4 pl-12" data-id="element-1124">
                          {statement.content}
                        </p>
                        {statement.scripture && <p className="text-[#0A1F44] text-sm font-medium pl-12" data-id="element-1125">
                            <BookOpen className="w-4 h-4 inline mr-2" data-id="element-1126" />
                            {statement.scripture}
                          </p>}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-[#0A1F44] text-white" data-id="element-1127">
        <div className="container-custom" data-id="element-1128">
          <SectionHeading title="Our Core Values" subtitle="RELATE" light data-id="element-1129" />

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto" data-id="element-1130">
            {coreValues.map((value, index) => <motion.div key={value.letter + index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl text-center border border-white/20 hover:bg-white/20 transition-colors" data-id="element-1131">
                <div className="text-4xl font-serif font-bold text-[#C8102E] mb-2" data-id="element-1132">
                  {value.letter}
                </div>
                <h4 className="font-bold text-white mb-2" data-id="element-1133">{value.word}</h4>
                <p className="text-white/60 text-xs tracking-wider uppercase" data-id="element-1134">
                  {value.scripture}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>
    </Layout>;
}